import { Square, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AudioWaveform } from "./AudioWaveform";
import { cn } from "@/lib/utils";

interface AudioRecorderProps {
  isRecording: boolean;
  duration: number;
  analyserNode: AnalyserNode | null;
  onStop: () => void;
  onCancel: () => void;
  className?: string;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function AudioRecorder({
  isRecording,
  duration,
  analyserNode,
  onStop,
  onCancel,
  className,
}: AudioRecorderProps) {
  if (!isRecording) return null;

  return (
    <div
      className={cn(
        "bg-utrecht-light-blue flex items-center gap-3 px-3 py-3 border-b border-utrecht-blue/20",
        className,
      )}
    >
      {/* Recording indicator */}
      <div className="flex items-center gap-2">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
        </span>
        <span className="text-muted-foreground w-12 text-sm font-medium tabular-nums">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Waveform */}
      <div className="min-w-0 flex-1">
        <AudioWaveform
          analyserNode={analyserNode}
          isRecording={isRecording}
          className="rounded-md"
        />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => {
            console.log("❌ Cancel button clicked");
            onCancel();
          }}
          className="h-8 w-8"
          aria-label="Cancel recording"
        >
          <X className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="destructive"
          onClick={() => {
            console.log("⏹️ Stop button clicked");
            onStop();
          }}
          className="h-8 w-8"
          aria-label="Stop recording"
        >
          <Square className="h-3 w-3 fill-current" />
        </Button>
      </div>
    </div>
  );
}
